/**
 * Per-field branching, authored in the form builder (TRUS-545).
 *
 * A field's conditional rule normally comes from field configuration and applies
 * wherever that field is used — one place owns the answer. This editor lets ONE
 * form depart from that, writing `visibility_rules_override`.
 *
 * The three states are deliberately distinct, and the UI has to keep them
 * distinct or the feature is untrustworthy:
 *
 *   inherit      override is null/undefined  — follows field configuration, and
 *                                              keeps following it as it changes
 *   always show  override with no conditions — unconditional ON THIS FORM, even
 *                                              where the shared rule would hide
 *   custom       override with conditions    — this form's own rule
 *
 * "Use the shared rule" DELETES the override rather than copying the shared rule
 * in, so a later change to field configuration flows through again. Copying
 * would silently fossilise today's rule, which is the failure this design exists
 * to avoid.
 */

import React from 'react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import type { FormFieldSchema, FormSectionSchema } from '@/services/api/forms';

/** Operators that need no comparison value. */
const VALUELESS = new Set(['is_populated', 'is_empty']);

/** [operator, i18n key, English fallback] — labels resolve at render time. */
const OPERATORS: Array<[string, string, string]> = [
  ['equals', 'forms.opIs', 'is'],
  ['not_equals', 'forms.opIsNot', 'is not'],
  ['in', 'forms.opIsAnyOf', 'is any of'],
  ['is_populated', 'forms.opHasValue', 'has a value'],
  ['is_empty', 'forms.opIsEmpty', 'is empty'],
  ['greater_than', 'forms.opGreaterThan', 'is greater than'],
  ['less_than', 'forms.opLessThan', 'is less than'],
  ['contains', 'forms.opContains', 'contains'],
];

type Rules = NonNullable<FormFieldSchema['visibility_rules_override']>;

export type BranchMode = 'inherit' | 'always' | 'custom';

export function branchMode(field: FormFieldSchema): BranchMode {
  const override = field.visibility_rules_override;
  if (override === undefined || override === null) return 'inherit';
  return (override.conditions || []).length === 0 ? 'always' : 'custom';
}

interface Props {
  field: FormFieldSchema;
  /** Every section in the template — a condition may depend on another section. */
  sections: FormSectionSchema[];
  onChange: (patch: Partial<FormFieldSchema>) => void;
}

export const FieldBranchEditor: React.FC<Props> = ({ field, sections, onChange }) => {
  const { t } = useAppTranslation();
  const mode = branchMode(field);
  const override = field.visibility_rules_override;
  const sharedRule = field.visibility_rules;
  const hasShared = !!(sharedRule && (sharedRule.conditions || []).length > 0);

  // Candidate dependency fields: everything in the template except this field
  // and the layout elements, which hold no value to test.
  const candidates = sections
    .flatMap((s) => s.fields || [])
    .filter(
      (f) =>
        f.field_id !== field.field_id &&
        !['section_header', 'divider', 'spacer', 'field_group'].includes(f.field_type as string)
    );

  const setMode = (next: BranchMode) => {
    if (next === 'inherit') {
      // Delete, never copy the shared rule in — see the file comment.
      onChange({ visibility_rules_override: null });
      return;
    }
    if (next === 'always') {
      onChange({ visibility_rules_override: { logic: 'AND', conditions: [] } });
      return;
    }
    onChange({
      visibility_rules_override: {
        logic: override?.logic || 'AND',
        conditions:
          (override?.conditions || []).length > 0
            ? override!.conditions
            : [{ field: candidates[0]?.field_id || '', operator: 'is_populated' }],
      },
    });
  };

  const patchRules = (patch: Partial<Rules>) =>
    onChange({
      visibility_rules_override: {
        logic: override?.logic || 'AND',
        conditions: override?.conditions || [],
        ...patch,
      },
    });

  const setCondition = (index: number, patch: Record<string, unknown>) => {
    const conditions = [...(override?.conditions || [])];
    conditions[index] = { ...conditions[index], ...patch };
    patchRules({ conditions });
  };

  return (
    <div className="rounded border border-gray-200 dark:border-gray-700 p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('forms.showThisField', 'Show this field')}</span>
        <select
          aria-label={t('forms.whenToShow', 'When to show this field')}
          className="h-7 text-[11px] px-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-600"
          value={mode}
          onChange={(e) => setMode(e.target.value as BranchMode)}
        >
          <option value="inherit">
            {hasShared
              ? t('forms.branchUseShared', 'using the shared rule')
              : t('forms.branchSharedDefault', 'always (shared default)')}
          </option>
          <option value="always">{t('forms.branchAlwaysHere', 'always, on this form')}</option>
          <option value="custom">{t('forms.branchOnlyWhen', 'only when…')}</option>
        </select>
      </div>

      {mode === 'inherit' && hasShared && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {t('forms.branchInheritHelp', 'Follows field configuration, and keeps following it if that rule changes.')}
        </p>
      )}

      {mode === 'always' && hasShared && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          {t('forms.branchAlwaysHelp', 'This form ignores the shared rule for this field.')}
        </p>
      )}

      {mode === 'custom' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-[11px]">
            <span className="text-gray-600 dark:text-gray-400">{t('forms.branchMatch', 'Match')}</span>
            <select
              aria-label={t('forms.branchMatchAria', 'Match all or any condition')}
              className="h-7 px-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-600"
              value={override?.logic || 'AND'}
              onChange={(e) => patchRules({ logic: e.target.value as 'AND' | 'OR' })}
            >
              <option value="AND">{t('forms.branchAll', 'all')}</option>
              <option value="OR">{t('forms.branchAny', 'any')}</option>
            </select>
            <span className="text-gray-600 dark:text-gray-400">{t('forms.branchOf', 'of:')}</span>
          </div>

          {(override?.conditions || []).map((cond, i) => (
            <div key={i} className="flex items-center gap-1 flex-wrap">
              <select
                aria-label={t('forms.branchCondField', 'Condition field')}
                className="h-7 text-[11px] px-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-600 max-w-[11rem]"
                value={cond.field || ''}
                onChange={(e) => setCondition(i, { field: e.target.value, value: '' })}
              >
                <option value="">{t('forms.branchSelectField', 'Select a field…')}</option>
                {candidates.map((f) => (
                  <option key={f.field_id} value={f.field_id}>
                    {f.label || f.field_id}
                  </option>
                ))}
              </select>

              <select
                aria-label={t('forms.branchCondOperator', 'Condition operator')}
                className="h-7 text-[11px] px-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-600"
                value={cond.operator}
                onChange={(e) => setCondition(i, { operator: e.target.value })}
              >
                {OPERATORS.map(([value, key, english]) => (
                  <option key={value} value={value}>
                    {t(key, english)}
                  </option>
                ))}
              </select>

              {!VALUELESS.has(cond.operator) && (
                <input
                  aria-label={t('forms.branchCondValue', 'Condition value')}
                  className="h-7 text-[11px] px-2 border rounded bg-white dark:bg-gray-800 dark:border-gray-600 w-28"
                  value={cond.value ?? ''}
                  onChange={(e) => setCondition(i, { value: e.target.value })}
                  placeholder={t('forms.branchValuePlaceholder', 'value')}
                />
              )}

              <button
                type="button"
                aria-label={t('forms.branchRemove', 'Remove condition')}
                className="h-7 px-2 text-[11px] text-red-600 hover:underline"
                onClick={() =>
                  patchRules({ conditions: (override?.conditions || []).filter((_, n) => n !== i) })
                }
              >
                {t('forms.branchRemoveShort', 'Remove')}
              </button>
            </div>
          ))}

          <button
            type="button"
            className="text-[11px] text-blue-600 hover:underline"
            onClick={() =>
              patchRules({
                conditions: [
                  ...(override?.conditions || []),
                  { field: candidates[0]?.field_id || '', operator: 'is_populated' },
                ],
              })
            }
          >
            {t('forms.branchAddCondition', '+ Add condition')}
          </button>

          {candidates.length === 0 && (
            <p className="text-[11px] text-gray-500">
              {t('forms.branchNeedsField', 'Add another field to this form first — a condition needs something to test.')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default FieldBranchEditor;
